// externals
import React from 'react';
import { useSelector } from 'react-redux';
import { XMarkIcon } from '@interstate/components/Icons';
import { Action } from '@interstate/components/Action';
import { Table } from '@interstate/components/Table';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { useVehicleProtectionContext } from '../../context/VehicleProtectionContext';

// store
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';

// styling
import {
    ProductSummaryWrapper,
    ProductSummaryLine,
    ProductSummaryRow,
    SummaryTableWrapper,
    ProductSummaryTotalCostLine,
    VppManualLegend
} from './ProductSummary.style';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// types
import type { DealProductSummary, DealProductWithProductCode, DraftDealProduct } from '@makemydeal/dr-dash-types';

type ProductSummaryTableProps = {
    summary: DealProductSummary;
    products: DraftDealProduct[];
    onCatalogProductRemove: (product: DealProductWithProductCode) => void;
    onManualProductRemove: (productId: string) => void;
};

const ProductSummaryTable = ({ summary, products, onCatalogProductRemove, onManualProductRemove }: ProductSummaryTableProps) => {
    const isTaxedIndicatorEnabled = useSelector(featureToggleSelectors.isInlineTaxedIndicatorEnabled);
    const showManualEditedLegend =
        useSelector(vehicleProtectionSelectors.getSelectedDivergedFromDarwinBaselineVppProducts).length > 0;
    const theme = useInterstateTheme();
    const isLocked = dealLockHooks.useIsDealLocked();
    const { isDarwinVPPEditDisabled } = useVehicleProtectionContext();
    const { totalProductCost, totalProductPrice } = summary;
    const selectedProducts = products.filter((product) => product.selected);

    // NOTE: read-only for now. isTaxed is kept current on the product by vehicleProtectionMiddleware from each
    // payment response; a product without a value is not taxed - there is no indeterminate state.
    const taxedColumn = {
        title: <span style={{ whiteSpace: 'nowrap' }}>Taxed</span>,
        dataIndex: 'isTaxed',
        width: '18%',
        render: (value: any) => (value ? 'Yes' : 'No')
    };

    const columns = [
        {
            title: 'Product Name',
            dataIndex: 'productName',
            width: isTaxedIndicatorEnabled ? '28%' : '40%'
        },
        ...(isTaxedIndicatorEnabled ? [taxedColumn] : []),
        {
            title: 'Cost',
            dataIndex: 'productCost',
            width: isTaxedIndicatorEnabled ? '22%' : '25%',
            render: (value: any) => formatDollarsAndCents(value)
        },
        {
            title: 'Price',
            dataIndex: 'productPrice',
            width: isTaxedIndicatorEnabled ? '22%' : '25%',
            render: (value: any) => formatDollarsAndCents(value)
        },
        {
            render: (_: any, record: DraftDealProduct) => {
                const productId = record.id || '';

                return (
                    <Action
                        actionIcon={{ icon: <XMarkIcon />, start: true }}
                        data-testid={`unselect-product-${productId}`}
                        onClick={() => {
                            if (record.isManualProduct) {
                                onManualProductRemove(productId);
                            } else {
                                onCatalogProductRemove(record as DealProductWithProductCode);
                            }
                        }}
                        sx={{ paddingTop: '4px' }}
                        disabled={isLocked || isDarwinVPPEditDisabled}
                    />
                );
            }
        }
    ];

    return (
        <ProductSummaryWrapper data-testid="product-summary-container">
            <ProductSummaryLine>
                {selectedProducts.length > 0 && (
                    <SummaryTableWrapper $hasTaxedColumn={isTaxedIndicatorEnabled}>
                        <Table
                            data-testid="vehicle-protection-table-summary"
                            id={'vehicle-protection-table-summary'}
                            columns={columns}
                            data={selectedProducts}
                            highlightOnHover={false}
                            dataDensity="small"
                        />
                    </SummaryTableWrapper>
                )}
            </ProductSummaryLine>
            <ProductSummaryTotalCostLine $showLegend={showManualEditedLegend}>
                {showManualEditedLegend && (
                    <VppManualLegend theme={theme} data-testid="vpp-manual-legend">
                        <Typography variant="body-sm" color="base.color.gray.700">
                            Manually Edited
                        </Typography>
                    </VppManualLegend>
                )}
                <ProductSummaryRow>
                    <span data-testid="product-summary-total-cost">Total Cost</span>
                    <span data-testid="product-summary-total-cost-value" className="product-amount">
                        {formatDollarsAndCents(totalProductCost)}
                    </span>
                </ProductSummaryRow>
            </ProductSummaryTotalCostLine>
            <ProductSummaryLine>
                <ProductSummaryRow>
                    <span data-testid="product-summary-total-price">Total Price</span>
                    <span className="product-amount">{formatDollarsAndCents(totalProductPrice)}</span>
                </ProductSummaryRow>
            </ProductSummaryLine>
        </ProductSummaryWrapper>
    );
};

export default ProductSummaryTable;
